import type { HomeworkTask } from '../types';
import { HomeworkTaskType } from '../types';

/** Nhãn hiển thị A, B, C, … theo thứ tự phương án (0 → A). */
export function getMcqOptionLabel(index: number): string {
    if (index < 0 || index > 25) return String(index + 1);
    return String.fromCharCode(65 + index);
}

/** Chỉ hiển thị nhãn A/B/C/D khi đúng 4 phương án (không áp dụng Đúng/Sai 2 ô). */
export function shouldShowMcqAbcdLabels(optionCount: number, isTrueFalse: boolean): boolean {
    return !isTrueFalse && optionCount === 4;
}

type Content = HomeworkTask['content'];

/**
 * Danh sách đáp án đúng dạng chuỗi (trùng phần tử trong mảng options gốc, dùng khi chấm / sau khi xáo trộn).
 * Ưu tiên chỉ số → nhãn một chữ → legacy correctAnswer (chuỗi đầy đủ).
 */
export function getMcqCorrectAnswerTexts(content: Content | undefined, options: string[] | undefined): string[] {
    if (!content || !options?.length) return [];
    const opts = options.map((o) => String(o).trim());

    const labelRaw = (content as { correctAnswerLabel?: string }).correctAnswerLabel;
    if (typeof labelRaw === 'string' && /^[A-Za-z]$/.test(labelRaw.trim())) {
        const idx = labelRaw.trim().toUpperCase().charCodeAt(0) - 65;
        if (idx >= 0 && idx < opts.length) return [opts[idx]];
    }

    if (content.correctAnswerIndex !== undefined && content.correctAnswerIndex !== null) {
        const i = Number(content.correctAnswerIndex);
        if (Number.isInteger(i) && i >= 0 && i < opts.length) return [opts[i]];
    }

    if (Array.isArray(content.correctAnswerIndices) && content.correctAnswerIndices.length > 0) {
        const texts: string[] = [];
        const seen = new Set<string>();
        for (const ix of content.correctAnswerIndices) {
            const i = Number(ix);
            if (Number.isInteger(i) && i >= 0 && i < opts.length) {
                const t = opts[i];
                if (!seen.has(t)) {
                    seen.add(t);
                    texts.push(t);
                }
            }
        }
        if (texts.length > 0) return texts;
    }

    const ca = content.correctAnswer;
    if (typeof ca === 'string' && /^[A-Za-z]$/.test(ca.trim()) && opts.length > 0) {
        const idx = ca.trim().toUpperCase().charCodeAt(0) - 65;
        if (idx >= 0 && idx < opts.length) return [opts[idx]];
    }

    if (Array.isArray(ca)) return ca.map(String).filter(Boolean);
    if (typeof ca === 'string' && ca.trim() !== '') return [ca.trim()];
    return [];
}

/**
 * Sau khi chỉnh sửa options hoặc nhập từ AI: gắn lại correctAnswerIndex/Indices và correctAnswer (chuỗi) cho khớp mảng hiện tại.
 */
export function syncMcqFieldsAfterOptionsChange(content: Content | undefined, taskType: string): Content | undefined {
    if (!content || !Array.isArray(content.options) || content.options.length === 0) return content;
    const opts = content.options.map((o) => String(o).trim());
    const texts = getMcqCorrectAnswerTexts({ ...content, options: opts }, opts);

    if (taskType === HomeworkTaskType.MultipleChoiceMulti || taskType === 'Trắc nghiệm (Nhiều đáp án)') {
        const indices = texts.map((t) => opts.indexOf(t)).filter((i) => i >= 0);
        const uniqIdx = [...new Set(indices)].sort((a, b) => a - b);
        const uniqTexts = uniqIdx.map((i) => opts[i]);
        return {
            ...content,
            options: opts,
            correctAnswerIndices: uniqIdx,
            correctAnswer: uniqTexts,
        };
    }

    if (taskType === HomeworkTaskType.MultipleChoiceSingle || taskType === 'Trắc nghiệm (1 đáp án)') {
        if (texts.length === 1) {
            const idx = opts.indexOf(texts[0]);
            if (idx >= 0) {
                return {
                    ...content,
                    options: opts,
                    correctAnswerIndex: idx,
                    correctAnswer: opts[idx],
                };
            }
        }
        return { ...content, options: opts };
    }

    return { ...content, options: opts };
}

/** Có ít nhất một câu trắc nghiệm / đúng sai → hiện chế độ “Làm bài kết hợp vận động”. */
export function homeworkSupportsMovementMode(tasks: HomeworkTask[] | undefined): boolean {
    if (!tasks?.length) return false;
    return tasks.some(
        (t) =>
            t.taskType === HomeworkTaskType.MultipleChoiceSingle ||
            t.taskType === HomeworkTaskType.MultipleChoiceMulti ||
            t.taskType === HomeworkTaskType.TrueFalse
    );
}

/** Có đáp án đúng khai báo để so khớp hiệu ứng trong chế độ vận động. */
export function movementTaskHasDefinedCorrectAnswer(task: HomeworkTask): boolean {
    const c = task.content;
    if (!c) return false;
    if (task.taskType === HomeworkTaskType.TrueFalse) {
        return c.correctAnswer !== undefined && c.correctAnswer !== null && String(c.correctAnswer).trim() !== '';
    }
    const opts = c.options || [];
    return getMcqCorrectAnswerTexts(c, opts).length > 0;
}

/** So khớp đáp án (trắc nghiệm 1/nhiều / đúng–sai) — dùng chung logic với màn chấm. */
export function isMcqTfStudentAnswerCorrect(task: HomeworkTask, studentAnswer: any): boolean {
    if (!task.content) return false;
    if (task.taskType === HomeworkTaskType.TrueFalse) {
        return String(studentAnswer) === String(task.content.correctAnswer);
    }
    if (task.taskType === HomeworkTaskType.MultipleChoiceSingle) {
        const opts = task.content.options || [];
        const correctTexts = getMcqCorrectAnswerTexts(task.content, opts);
        return correctTexts.length === 1 && String(studentAnswer) === correctTexts[0];
    }
    if (task.taskType === HomeworkTaskType.MultipleChoiceMulti) {
        const correctTexts = getMcqCorrectAnswerTexts(task.content, task.content?.options || []);
        const correctSet = new Set(correctTexts.map(String));
        const studentSet = new Set(Array.isArray(studentAnswer) ? studentAnswer.map(String) : []);
        return (
            correctSet.size > 0 &&
            correctSet.size === studentSet.size &&
            [...correctSet].every((val) => studentSet.has(val))
        );
    }
    return false;
}
